import {
  startAuthentication,
  startRegistration,
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
} from '@simplewebauthn/browser'
import { apiClient } from '../api/client'

// Tokens returned by a successful passkey login/finish. Mirrors the
// email/password login response shape so AuthContext.loginWithTokens can
// consume it directly.
export interface PasskeyLoginTokens {
  access_token: string
  refresh_token: string
  user: any
  expires_in: number
}

export interface AuthProvider {
  name: string
  type: string
}

export interface ProvidersResponse {
  data: AuthProvider[]
  passkeys_enabled: boolean
}

export { browserSupportsWebAuthn, browserSupportsWebAuthnAutofill }

// fetchProviders loads the enabled social providers + passkey availability.
export async function fetchProviders(): Promise<ProvidersResponse> {
  const res = await apiClient.GET('/api/v1/auth/providers')
  if (res.error || !res.data) {
    throw new Error('Failed to load auth providers')
  }
  return res.data as unknown as ProvidersResponse
}

// providerLoginUrl builds the full-page redirect URL that starts a social
// login. The browser navigates here directly (not via XHR); the callback
// returns to redirectUri with tokens in the query string.
export function providerLoginUrl(providerType: string, org: string, redirectUri: string): string {
  const params = new URLSearchParams({ org, redirect_uri: redirectUri })
  return `/api/v1/auth/${providerType}/login?${params.toString()}`
}

// startPasskeyLogin runs the WebAuthn assertion ceremony and returns session
// tokens. When useAutofill is true it drives the conditional-UI (autofill)
// flow — the promise resolves only once the user picks a passkey from the
// browser's autofill suggestions.
export async function startPasskeyLogin(opts: {
  email?: string
  org?: string
  useAutofill?: boolean
}): Promise<PasskeyLoginTokens> {
  const begin = await apiClient.POST('/api/v1/auth/passkeys/login/begin', {
    body: opts.email ? { email: opts.email } : {},
  })
  if (begin.error || !begin.data) {
    throw new Error('Failed to start passkey login')
  }
  const { options, session } = begin.data as any

  const assertion = await startAuthentication({
    optionsJSON: options.publicKey,
    useBrowserAutofill: opts.useAutofill === true,
  })

  const finish = await apiClient.POST('/api/v1/auth/passkeys/login/finish', {
    body: { session, credential: assertion as any, org: opts.org },
  })
  if (finish.error || !finish.data) {
    throw new Error('Passkey verification failed')
  }
  return finish.data as unknown as PasskeyLoginTokens
}

// passkeyAddErrorKey picks the auth i18n key explaining why adding a passkey
// failed, from the API problem code. A server without WebAuthn configured
// answers WEBAUTHN_NOT_CONFIGURED: « try again » would never work (#1705).
export function passkeyAddErrorKey(code: string | null): string {
  if (code === 'mfa_verification_required') return 'mfa.errors.verification_required'
  if (code === 'WEBAUTHN_NOT_CONFIGURED') return 'passkeys.notConfigured'
  return 'passkeys.addFailed'
}

// registerPasskey runs the registration ceremony for the signed-in user and
// returns the stored passkey projection.
export async function registerPasskey(name: string): Promise<any> {
  const begin = await apiClient.POST('/api/v1/auth/passkeys/register/begin')
  if (begin.error || !begin.data) {
    // Keep the problem code: a session that isn't MFA-verified may not add
    // a factor next to existing ones (`mfa_verification_required`).
    throw new Error(JSON.stringify(begin.error ?? { title: 'Failed to start passkey registration' }))
  }
  const { options, session } = begin.data as any

  const credential = await startRegistration({ optionsJSON: options.publicKey })

  const finish = await apiClient.POST('/api/v1/auth/passkeys/register/finish', {
    body: { session, credential: credential as any, name },
  })
  if (finish.error || !finish.data) {
    throw new Error('Passkey registration failed')
  }
  return (finish.data as any).passkey
}

// answerMfaWithPasskey runs the WebAuthn assertion answering the passkey
// entry of an MFA challenge (spec 2026-10-01-04): the options come from the
// challenge, the browser asks for the user's passkey with user verification,
// and the result goes to verifyMfa with factor "passkey".
export async function answerMfaWithPasskey(
  begin: () => Promise<{ options: Record<string, any>; session: string }>,
): Promise<{ session: string; credential: Record<string, unknown> }> {
  const { options, session } = await begin()
  const credential = await startAuthentication({ optionsJSON: options.publicKey })
  return { session, credential: credential as unknown as Record<string, unknown> }
}
